import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'navy' | 'secondary' | 'ghost' | 'danger' | 'outline'
export type ButtonSize = 'sm' | 'md' | 'lg'

/**
 * Labels are never truncated and buttons never take a fixed width — Russian
 * runs up to 40% longer than English and Uzbek up to 30% (§11.1 principle 3).
 *
 * The brand is square-cornered, so these carry a 3px radius, and the primary
 * fill is the guideline teal under dark ink rather than white: white on
 * #39C2D7 scores 2.0 and fails AA, the navy ink scores 7.7.
 */
const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-itpu-teal text-on-brand hover:brightness-[1.06] active:brightness-95 border border-transparent',
  navy: 'bg-itpu-navy text-itpu-white hover:brightness-125 active:brightness-95 border border-transparent',
  secondary:
    'bg-surface text-text border border-border hover:border-border-strong hover:bg-surface-tint',
  outline: 'bg-transparent text-text-link border border-itpu-teal hover:bg-brand-50',
  ghost: 'bg-transparent text-text-body border border-transparent hover:bg-surface-tint',
  danger:
    'bg-danger-solid text-white hover:brightness-110 active:brightness-95 border border-transparent',
}

const SIZES: Record<ButtonSize, string> = {
  sm: 'min-h-8 px-3 py-[5px] text-meta gap-1.5',
  md: 'min-h-10 px-4 py-2 text-body gap-2',
  lg: 'min-h-12 px-6 py-3 text-body gap-2',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  fullWidth?: boolean
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    fullWidth = false,
    leadingIcon,
    trailingIcon,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center rounded-sm font-semibold transition duration-fast ease-standard',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 aria-hidden className="size-4 animate-spin" /> : leadingIcon}
      {children}
      {!loading && trailingIcon}
    </button>
  )
})

/** Shared class string so an <a> or <Link> can wear the button's clothes. */
export function buttonClass(
  variant: ButtonVariant = 'primary',
  size: ButtonSize = 'md',
  fullWidth = false,
): string {
  return cn(
    'inline-flex items-center justify-center rounded-sm font-semibold transition duration-fast ease-standard',
    VARIANTS[variant],
    SIZES[size],
    fullWidth && 'w-full',
  )
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required: an icon-only control is invisible to a screen reader without it. */
  label: string
  variant?: ButtonVariant
  size?: ButtonSize
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, variant = 'ghost', size = 'md', className, children, type = 'button', ...rest },
  ref,
) {
  const box = size === 'sm' ? 'size-8' : size === 'lg' ? 'size-12' : 'size-10'
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-sm transition duration-fast ease-standard',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        box,
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  )
})
